import { Box, Dialog, DialogActions, DialogContent } from "@mui/material";
import { MprcDialogTitleClose, MprcRequestErrorDialog, MprcRequestIndicator } from "components";
import { ButtonCancel, ButtonSave } from "components/buttons";
import { AutoCompleteHierarchicalModel } from "components/MprcAutoCompleteHierarchical";
import { GetQuestionClassifyPublicationDto, TopicAutoCompleteHierarchicalModelDto, UpdateQuestionClassifyPublicationCommand } from "generated/CoreApi_v1";
import LibUtils from "lib/LibUtils";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { NIL } from "uuid";
import { PublicationTopicsBox } from "views/knowledge/publication/creation/components";
import { IStickerBoxValidation, PublicationStickerBox } from "views/knowledge/publication/creation/components/PublicationStickerBox";
import { parseContentTopics } from "views/knowledge/publication/creation/CreatePost";


////////////////////////////////////////////////////////////////////////////////

interface IState {
    record: GetQuestionClassifyPublicationDto;
    validation: IStickerBoxValidation;
}

////////////////////////////////////////////////////////////////////////////////

interface IPros {
    publicationId: string;
    title?: string;
    onClose: (changed: boolean) => void;
}

/////////////////////////////////////////////////////////////////////////////////

export const ClassifyQuestion = (props: IPros) => {
    const { publicationId, title, onClose } = props;

    const api = useCoreApi();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation("knowledge");

    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const [canSave, setCanSave] = useState<boolean>(false);
    const [contentTopics, setContentTopics] = useState<AutoCompleteHierarchicalModel[]>([]);
    const [viewError, setViewError] = useState<any>();
    const [loadDto, setLoadDto] = useState<GetQuestionClassifyPublicationDto>();
    const [allowStickers, setAllowStickers] = useState<boolean>(false);

    const [state, setState] = useState<IState>(
        {
            record: {
                publicationId: NIL,
                title: "",
                content: "",
                topics: [],
                channel: null!,
                isPrivate: false,
                advancedOptions: {
                    isInstitutional: false,
                    viewPeriodStart: null,
                    viewPeriodEnd: null,
                    viewStickerUntil: null,
                    sticker: null
                },
            },
            validation: {
                viewStickerUntil: true
            },
        }
    );

    const { record, validation } = state;

    ///
    const updateRecord = (data: Partial<GetQuestionClassifyPublicationDto>) => {
        return { ...state.record, ...data };
    };

    const parseTopics = (record: GetQuestionClassifyPublicationDto) => {
        const currentTopics = record.topics;
        const topics = parseContentTopics("", []);

        topics.filter(p => p.selectable).forEach((topic) => {
            if (currentTopics.find(p => p.key == topic.key) == null && currentTopics.length < 5) {
                currentTopics.push(topic as TopicAutoCompleteHierarchicalModelDto);
                topic.selectable = false;
            }
        });

        setContentTopics(topics);
    };


    ////////////////////////////////////////////////////////////////////////////
    /// EFFECT's
    ////////////////////////////////////////////////////////////////////////////

    // Ao carregar
    useEffect(() => {
        async function getData() {
            try {
                var result = await api.v1.knowledgePublication.getQuestionClassifyPublication({ publicationId });

                const advSetting = await api.v1.knowledgePublication.getAdvancedOptionsSettings({ channelId: result.data.channel.channelId });
                setAllowStickers(advSetting.data.allowStickers);

                const record = result.data;
                setState({
                    record,
                    validation: {
                        viewStickerUntil: true,
                    }
                });

                setLoadDto(LibUtils.deepCopy(record));

                parseTopics(record);
            }
            catch (e) {
                setViewError(e as Error);
            }
            finally {
                setLoading(false);
            }
        }

        if (loading) getData();
    }, [loading]);


    // Ao salvar
    useEffect(() => {
        async function postData() {
            try {
                const dto: UpdateQuestionClassifyPublicationCommand = {
                    publicationId: record.publicationId,
                    channelId: record.channel.channelId,
                    topics: record.topics.map((item) => item.key),
                    advancedOptions: {
                        isInstitutional: record.advancedOptions.isInstitutional,
                        stickerId: record.advancedOptions.sticker?.stickerId ?? null,
                        viewStickerUntil: record.advancedOptions.viewStickerUntil,
                        viewPeriodEnd: record.advancedOptions.viewPeriodEnd,
                        viewPeriodStart: record.advancedOptions.viewPeriodStart,
                    }
                };

                const response = await api.v1.knowledgePublication.updateQuestionClassifyPublication(dto);
                if (response.data) {
                    enqueueSnackbar(t("Publication.ClassifyDialog.SuccessMessage"), { variant: "success" });
                    onClose(response.data);
                }
            }
            catch (e) {
                setViewError(e);
            }
            finally {
                setSaving(false);
            }
        }

        if (saving) postData();

    }, [saving]);

    // Ao editar os dados
    useEffect(() => {
        const isRecordChanged = !LibUtils.deepCompare(state.record, loadDto);
        setCanSave(isRecordChanged);
    }, [state]);


    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <>
            <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(undefined)} />

            <Dialog
                open
                fullWidth
                maxWidth="sm"
                onClose={() => onClose(false)}
            >

                <MprcDialogTitleClose
                    title={t("Publication.ClassifyDialog.Title")}
                    subtitle={title}
                    shrinkPadding={true}
                    onClose={() => onClose(false)}
                />

                <DialogContent>

                    <MprcRequestIndicator visible={loading || saving} type={loading ? "request" : "post"} />

                    <PublicationTopicsBox
                        publication={record}
                        title={t("Publication.ClassifyDialog.TopicBoxTitle")}
                        topics={record.topics as AutoCompleteHierarchicalModel[]}
                        suggestions={contentTopics}
                        onUpdateState={
                            (topics) => setState({
                                ...state,
                                record: updateRecord({ topics: topics as TopicAutoCompleteHierarchicalModelDto[] })
                            })
                        }
                    />
                    <Box mt={1}>
                        {
                            allowStickers &&
                            <PublicationStickerBox
                                advancedOptions={record.advancedOptions}
                                enabled={true}
                                onUpdateState={({ sticker, viewStickerUntil }) => {
                                    setState({
                                        ...state,
                                        record: updateRecord({ advancedOptions: { ...record.advancedOptions, sticker, viewStickerUntil } }),
                                        validation: {
                                            viewStickerUntil:
                                                sticker == null
                                                || sticker == undefined
                                                || !sticker.requiredDeadline
                                                || (sticker.requiredDeadline && viewStickerUntil != null && LibUtils.isValidDate(viewStickerUntil))
                                        }
                                    });
                                }}
                                validation={validation}
                            />
                        }
                    </Box>

                </DialogContent>

                <DialogActions>
                    <ButtonCancel
                        disabled={loading}
                        variant="contained"
                        onClick={() => onClose(false)}
                    />
                    <ButtonSave
                        disabled={loading || !canSave}
                        variant="contained"
                        onClick={() => setSaving(true)}
                    />
                </DialogActions>
            </Dialog>
        </>
    );
};
